/**
 * ReturnToOriginButton — the way back to the arche that handed this document
 * to Document Sign.
 *
 * Shown only on envelopes that arrived through a `sign_document` handoff
 * (`originArcheId` set). Pressing it hands a `sign_document_result` REPLY back
 * to that arche, so the caller can reconcile its own record.
 *
 * Deliberately manual. An automatic redirect after Send would bounce the user
 * out before they see the confirmation, which makes "did it actually send?"
 * unanswerable from the arche they land back in — the opposite of the audit
 * trail this whole path exists to give them.
 *
 * The reply is a POINTER, not a result record: it carries the requestId, the
 * outcome, and (for a real envelope) its id — never recipients, statuses, or
 * timeline. Navigation state is same-origin forgeable, so a consumer that
 * trusted it could have a false "signed" row planted in a legal approval
 * trail. The receiving arche matches on `requestId`, consumes once, and reads
 * the authoritative state under its own authorization.
 */

import { useCallback, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGlobalFiasAIOptional } from '@/components/fias-ai';
import { toast } from '@/components/ui/use-toast';
import { addBreadcrumb } from '@/lib/telemetry';
import type { DocumentSignEnvelopeResponse } from '@/lib/api';

export type SignDocumentOutcome = 'sent' | 'saved_draft' | 'cancelled';

interface ReturnToOriginButtonProps {
  envelope: Pick<
    DocumentSignEnvelopeResponse,
    'envelopeId' | 'originArcheId' | 'originRequestId' | 'originCorrelationId'
  >;
  outcome: SignDocumentOutcome;
  label?: string;
  variant?: 'default' | 'outline' | 'ghost';
  className?: string;
  /**
   * Flush whatever the host page is holding, and say whether it is safe to
   * leave. Returning false aborts the return — the page keeps the user with
   * its own error visible.
   *
   * REQUIRED in spirit on any page with unsaved state. This handoff navigates
   * the whole host away, so an exit that skips the flush discards the user's
   * work silently: no error, no undo, and the arche they return to is told
   * the draft was saved. The builder found that out the hard way.
   */
  beforeLeave?: () => Promise<boolean>;
}

export function ReturnToOriginButton({
  envelope,
  outcome,
  label = 'Back to the app that sent this',
  variant = 'outline',
  className,
  beforeLeave,
}: ReturnToOriginButtonProps) {
  const ai = useGlobalFiasAIOptional();
  // `beforeLeave` saves over the network, so there is a real window where the
  // button is still live and doing nothing visible. Without this a double
  // click runs the flush twice and pushes two history entries for one intent.
  const [leaving, setLeaving] = useState(false);
  const { originArcheId, originRequestId } = envelope;

  const handleReturn = useCallback(async () => {
    if (!ai || !originArcheId || !originRequestId || leaving) return;
    setLeaving(true);
    try {
      // Flush BEFORE routing. Once openHandoff navigates, this page is gone
      // and anything it was holding goes with it.
      if (beforeLeave && !(await beforeLeave())) {
        setLeaving(false);
        return;
      }
      // `cancelled` structurally carries no envelopeId — nothing was kept, so
      // there is nothing to point at. The union enforces that shape on both
      // sides of the wire; building it here by branching keeps it honest.
      const payload =
        outcome === 'cancelled'
          ? ({
              kind: 'sign_document_result' as const,
              outcome: 'cancelled' as const,
              requestId: originRequestId,
              ...(envelope.originCorrelationId
                ? { correlationId: envelope.originCorrelationId }
                : {}),
            } as const)
          : ({
              kind: 'sign_document_result' as const,
              outcome,
              requestId: originRequestId,
              envelopeId: envelope.envelopeId,
              ...(envelope.originCorrelationId
                ? { correlationId: envelope.originCorrelationId }
                : {}),
            } as const);
      // `openHandoff` returns false when the payload has nowhere valid to go.
      // Ignoring that made a failed return indistinguishable from a working one:
      // the user pressed a button labelled "back to the app that sent this" and
      // stayed exactly where they were, with nothing said and nothing recorded.
      // The arche they came from is then waiting on a result that will never
      // arrive, which is the one outcome this whole leg exists to prevent.
      const routed = ai.openHandoff(payload, originArcheId);
      if (!routed) {
        addBreadcrumb('document-sign', 'Return handoff was not routed', {
          originArcheId,
          outcome,
        });
        toast({
          title: 'Couldn’t go back to that app',
          description: 'Open it from your apps list — this envelope is saved either way.',
          variant: 'destructive',
        });
        // Routing failed, so this page is staying — let them try again.
        setLeaving(false);
      }
    } catch (err) {
      // The flush saves over the network and can reject. Swallowing that
      // would leave the user clicking a button that re-enables and says
      // nothing — the same silent failure the routing branch above exists to
      // prevent, arriving by a different door.
      addBreadcrumb('document-sign', 'Return handoff threw before routing', {
        originArcheId,
        outcome,
        error: err instanceof Error ? err.message : String(err),
      });
      toast({
        title: 'Couldn’t save before leaving',
        description: 'Your changes are still here — try again.',
        variant: 'destructive',
      });
      setLeaving(false);
    }
    // No `finally`: on the success path the host is navigating away and
    // re-enabling would flash the button live again mid-transition.
  }, [ai, beforeLeave, envelope, leaving, originArcheId, originRequestId, outcome]);

  // No origin, no return. An envelope started inside Document Sign has
  // nowhere to go back to, and rendering a dead button would be worse than
  // rendering nothing.
  if (!originArcheId || !originRequestId || !ai) return null;

  return (
    <Button
      type="button"
      variant={variant}
      className={className}
      disabled={leaving}
      onClick={() => void handleReturn()}
    >
      <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
      {label}
    </Button>
  );
}
